// src/pages/BillDetailPage.tsx
//
// The pro-ration split and its PROVENANCE: which rule id and version, at what
// rate, and which service date it was selected on — the build brief's core
// ask for this screen. Entitlement is always resolved against `serviceDate`,
// never `submissionDate` (ENGINEERING.md §1 — "the misreading to prevent").
//
// The provenance panel renders exactly two states, never a third:
//   1. Computed            — bill.appliedRule/subsidy/coPayment all present.
//   2. Not computed        — offers the compute the gateway would actually
//                            accept from THIS officer, in the mode this bill
//                            qualifies for. See `ComputePanel`, which
//                            distinguishes an ordinary compute from a REPAIR of
//                            a bill that has already been decided.
//
// ⚠ A FAILED ATTEMPT IS NOT A THIRD STATE, and treating it as one was a defect.
// The failure card replaced `ComputePanel` outright, so on the repair path the
// warning alert, the repair framing, the amount and the two-step confirm all
// vanished and were replaced by a plain "Try again" wired straight to the
// compute — one click, on an already-approved bill, with none of the
// interruption `ComputePanel` documents as the reason the repair looks
// different. The server's reason is now rendered INSIDE the panel, above
// whichever surface the bill actually has, so a retry is always the same
// gesture the first attempt was.
//
// The failure is still purely client-side and transient: `POST .../compute`
// never writes a partial row on failure (see its own doc comment in
// services/gateway/src/routes/bills.ts), so there is nothing on the bill itself
// to read it back from — it exists only for the duration of this page's attempt.
import { plainProse } from '../lib/plain-words'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { SgdsAlert, SgdsButton, SgdsTextarea, SgdsTooltip } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useAuth } from '../state/AuthContext'
import { targetValue } from '../lib/sgds-dom'
import {
  billAuthority,
  computeOffer,
  computeUnavailabilitySentence,
  noComputeCopy,
  noComputeGround,
  unavailabilitySentence,
  type BillAuthority,
  type ComputeOffer,
} from '../lib/authority'
import {
  isSendableRejectReason,
  rejectReasonMessage,
  rejectReasonProblem,
  rejectReasonHint,
  officerCommentHint,
} from '../lib/reject-reason'
import { publishedMaxLength, type EntitlementBasis, type ResolvedBasis } from '../lib/types'
import { BillStatusBadge, CategoryBadge, StateBadge } from '../components/StatusBadge'
import { BillHistory } from '../components/BillHistory'
import { ReprorationHistory } from '../components/ReprorationHistory'
// ★★ WAVE 10 — the pause, its return path, and the referral (Annex A 37/38).
// Its own card rather than more buttons in `DecisionCard`; see that file's header.
import { BillPauseCard } from '../components/BillPauseCard'
// ★★ WAVE 1c — Annex A 3/4, composed as a delivery rather than written as an effect.
import { AdjustmentComposer } from '../components/AdjustmentComposer'
import { ErrorCard, LoadingCard, EmptyCard } from '../components/StateCards'
import { Money } from '../components/Money'
import { RateProvenanceNote } from '../components/RateProvenance'
import { MsoBillComparison } from '../components/MsoBillComparison'
import { mayAdministerReferenceData } from '@prs2/auth'
import { billStatusLabel, billTypeLabel, formatBasisPoints, formatCents, formatInstant, formatIsoDate } from '../lib/format'
import type { AppliedRule, Bill, BillAdjustment, BillPaymentEvent, Scope } from '../lib/types'

/**
 * A type guard, not a plain boolean: TypeScript's control-flow narrowing only
 * follows a discriminant checked INLINE in the branch condition (or through a
 * user-defined guard like this one) — a plain `const isComputed = bill.appliedRule
 * !== null && …` computed once and reused in a ternary does not narrow
 * `bill.appliedRule` back to non-null inside that branch under `strictNullChecks`.
 * All three provenance fields are checked together (I2's own invariant, enforced
 * again here rather than assumed): `appliedRule`/`subsidy`/`coPayment` are `null`
 * or non-`null` in lockstep, never a partial mix, per `toBillRow` and
 * `bill_provenance_consistent`.
 */
function isComputedBill(
  bill: Bill,
): bill is Bill & { appliedRule: AppliedRule; subsidy: number; coPayment: number } {
  return bill.appliedRule !== null && bill.subsidy !== null && bill.coPayment !== null
}

/**
 * The whole-day difference between this bill's service date and its
 * submission date, computed from the actual bill rather than stated as a
 * blanket fact (M2). Every one of the 40 synthetic seed bills happens to have
 * the two dates a day apart, which is a property of that fixture, not a rule
 * of this system — a bill entered any other way could differ by any number of
 * days, in either direction, or not at all.
 */
function serviceSubmissionNote(serviceDateIso: string, submissionDateIso: string): string {
  const toUtcDayCount = (iso: string): number | null => {
    const [year, month, day] = iso.split('-').map(Number)
    if (year === undefined || month === undefined || day === undefined) return null
    return Date.UTC(year, month - 1, day) / 86_400_000
  }
  const serviceDays = toUtcDayCount(serviceDateIso)
  const submissionDays = toUtcDayCount(submissionDateIso)
  if (serviceDays === null || submissionDays === null) return ''

  const diff = submissionDays - serviceDays
  if (diff === 0) return 'On this bill, the service date and the submission date are the same day.'
  const magnitude = Math.abs(diff)
  const unit = magnitude === 1 ? 'day' : 'days'
  return diff > 0
    ? `On this bill, the submission date is ${magnitude} ${unit} after the service date.`
    : `On this bill, the submission date is ${magnitude} ${unit} before the service date.`
}

/**
 * ★★★ THE SENTENCE THAT MAKES THE DATE RULE A NUMBER RATHER THAN A CLAIM.
 *
 * On most bills the two dates fall inside one entitlement period, service-dating
 * and submission-dating agree, and there is nothing to say — this returns null
 * and the paragraph reads exactly as it did before.
 *
 * On a bill where the officer's entitlement changed BETWEEN the two dates, the
 * rule decides real money, and the screen says so in the register's own terms.
 *
 * ⚠ EVERY WORD OF THE BASIS COMES FROM THE SERVER. This function decides only
 * how to phrase what it was handed; it never resolves an entitlement, never
 * infers one from the two dates, and returns null the moment the server sent no
 * basis — an older gateway produces no sentence rather than a guessed one.
 */
export function entitlementBasisNote(basis: EntitlementBasis | undefined): string | null {
  if (basis === undefined || !basis.differs) return null
  const { atService, atSubmission } = basis
  // `differs` is only ever true with both sides resolved; this is belt-and-braces
  // so a malformed payload renders nothing rather than "undefined ward undefined".
  if (atService === null || atSubmission === null) return null
  const name = (side: ResolvedBasis): string => `${side.scheme} ward ${side.wardClass}`
  return (
    `⚠ This officer's entitlement changed between those two dates: ` +
    `${name(atService)} on the service date, ${name(atSubmission)} on the submission date. ` +
    `This bill is priced under ${atService.scheme}: the scheme in force on the day of medical service.`
  )
}

type ComputeAttempt = { readonly kind: 'idle' } | { readonly kind: 'computing' } | { readonly kind: 'failed'; readonly message: string }

type LifecycleAttempt = { readonly kind: 'idle' } | { readonly kind: 'submitting' } | { readonly kind: 'failed'; readonly message: string }

/**
 * The route element for `/bills/:reference`.
 *
 * ⚠ The `key` is the whole point, and it fixes a real cross-bill state leak.
 * React Router keeps the SAME element mounted when only a route parameter
 * changes, so navigating from one bill to another re-ran the fetch but kept
 * every `useState` in `BillDetailPage` intact: a half-typed rejection reason, a
 * failed compute attempt's error message, an open reject form. One bill's
 * "Could not be computed — no entitlement covers OFF-16 on 2026-05-18" rendered
 * under the next bill's reference, describing a failure that never happened to
 * it.
 *
 * Keying on the reference makes React unmount and remount the page, which
 * discards that state by construction rather than by remembering to clear each
 * field in an effect — the version of this fix that goes stale the next time
 * someone adds a `useState`.
 */
export function BillDetailRoute() {
  const { reference } = useParams<{ reference: string }>()
  return <BillDetailPage key={reference ?? ''} />
}

export function BillDetailPage() {
  const { reference } = useParams<{ reference: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const result = useApi(() => api.bill(reference ?? ''), [reference])
  // A SEPARATE request, deliberately. The ledger is a different table behind a
  // different repository with its own failure mode, and folding it into the
  // bill fetch would mean a history that failed to load took the whole screen
  // down with it — or, worse, rendered as "no decision recorded yet", which is
  // a statement about the bill rather than about the request. It also keeps
  // each card inside its own response-time class (ux-direction §1.4: no screen
  // is a single composite query).
  const history = useApi(() => api.billTransitions(reference ?? ''), [reference])
  // ★ PHASE 10 (Tasks 36, 38) — two more SEPARATE requests, for the same
  // reason `history` is separate from `result`: each is a different table
  // behind a different repository, and a failure in one must not take the
  // others down with it.
  const adjustments = useApi(() => api.billAdjustments(reference ?? ''), [reference])
  /**
   * ★★★ WAVE 13 (P7) - the re-proration history (P2C1 §12, RA row 7).
   *
   * ⚠ A SEPARATE ledger from the adjustments above and deliberately not merged
   * into one "changes" table. An ADJUSTMENT changes the bill's AMOUNT because a
   * provider corrected what it charged; a RE-PRORATION re-divides the SAME
   * amount because the officer's entitlement record changed. A reader who could
   * not tell the two apart could not answer the only question either of them
   * raises: was the officer billed more because the treatment cost more, or
   * because their benefit changed?
   */
  const reprorations = useApi(() => api.billReprorations(reference ?? ''), [reference])
  const payments = useApi(() => api.billPayments(reference ?? ''), [reference])

  const [computeAttempt, setComputeAttempt] = useState<ComputeAttempt>({ kind: 'idle' })
  const [lifecycleAttempt, setLifecycleAttempt] = useState<LifecycleAttempt>({ kind: 'idle' })
  const [showRejectForm, setShowRejectForm] = useState(false)
  /**
   * ★★★ WAVE 24 — APPROVE IS CONFIRMED HERE TOO.
   *
   * On this page Approve fired on a SINGLE click while Reject, six lines below
   * it, was two-step — and the same approval on `ApprovalQueuePage` opens a
   * modal. So one act, authorising the payment of public money, had three
   * different amounts of friction depending on which surface the officer
   * happened to be on, and the cheapest one was the money-moving one.
   *
   * ⚠ An inline two-step rather than a modal, because that is THIS page's own
   * idiom for a decision (see `showRejectForm`), and an officer meeting two
   * different confirmation shapes on one screen learns neither.
   */
  const [showApproveConfirm, setShowApproveConfirm] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  /**
   * ★★ WAVE 9 — Annex A 37/38's approval comment, shared by all three decisions on
   * this screen. One piece of state and not three: the officer is deciding ONE bill
   * and the comment belongs to whichever decision they take, so three fields would
   * be three chances to type a remark into the box the button does not read.
   */
  const [officerComment, setOfficerComment] = useState('')
  // The repair is deliberately two clicks (see `ComputePanel`). An ordinary
  // compute stays one.
  //
  // ⚠ Reset on SUCCESS and on Cancel, and deliberately not on failure: a refused
  // repair leaves the officer looking at the confirm box with the server's
  // reason above it, which is what this flag says is on screen. That was a
  // contradiction only while a failure replaced the panel with a generic error
  // card — the defect `ComputePanel`'s header records.
  const [confirmingRepair, setConfirmingRepair] = useState(false)

  if (reference === undefined) {
    return <ErrorCard title="No bill reference given" message="This route requires a bill reference." />
  }

  // `initialLoading`, not `loading`: a compute or a lifecycle action calls
  // `result.reload()`, and swapping the whole page for a skeleton on every
  // refresh both flashes the screen and throws away whatever had keyboard
  // focus. A refresh keeps the rendered bill in place; only a first load has
  // nothing to keep. See `useApi`'s own comment.
  if (result.initialLoading) {
    return <LoadingCard label={`Loading bill ${reference}`} rows={6} />
  }

  if (result.error !== null) {
    const notFound = result.error.includes('no bill')
    return (
      <ErrorCard
        title={notFound ? 'Bill not found in your scope' : 'Could not load this bill'}
        message={
          notFound
            ? `Either "${reference}" does not exist, or your active identity's grants do not cover it. A bill you cannot see is reported identically to one that does not exist.`
            : result.error
        }
        reference={reference}
        {...(notFound ? {} : { onRetry: result.reload })}
      />
    )
  }

  const bill = result.data?.bill
  if (bill === undefined) return null

  // ⚠ Which compute this bill would be, and whether THIS officer may perform
  // it — not a status test. `POST /.../compute` serves two operations and asks
  // two different authorisation questions (`COMPUTE`, level 0, ceiling exempt;
  // `COMPUTE_RECOVERY`, level 2, ceiling binding), so a status-only gate would
  // once again offer a button the server refuses — the I7 defect. `null` means
  // no compute is possible on this bill in any mode.
  const offer = user?.scope != null ? computeOffer(user.scope, bill) : null

  const handleCompute = async () => {
    setComputeAttempt({ kind: 'computing' })
    try {
      await api.computeBill(bill.reference)
      setComputeAttempt({ kind: 'idle' })
      setConfirmingRepair(false)
      result.reload()
      // ⚠ THE LEDGER IS DELIBERATELY *NOT* RELOADED HERE, and the comment that
      // used to sit in its place justified a call that did nothing.
      //
      // A compute — ordinary or repair — appends nothing to
      // `app.bill_transition` (`recordProration` writes the four provenance
      // columns and `computed_at`, and touches neither `status` nor the ledger),
      // and `BillHistory` consumes exactly two things: `transitions`, which
      // cannot have changed, and `billStatus`, which it receives from THIS
      // page's `bill` and therefore already refreshes with `result.reload()`
      // above. So the second request could not change a pixel. A lifecycle
      // action is the opposite case and still reloads it — see
      // `runLifecycleAction`, where the ledger genuinely gained a row.
    } catch (error) {
      setComputeAttempt({
        kind: 'failed',
        message: error instanceof ApiError ? error.message : 'Could not compute this bill.',
      })
    }
  }

  const runLifecycleAction = async (action: () => Promise<unknown>, fallbackMessage: string) => {
    setLifecycleAttempt({ kind: 'submitting' })
    try {
      await action()
      setLifecycleAttempt({ kind: 'idle' })
      setShowRejectForm(false)
      setRejectReason('')
      setOfficerComment('')
      result.reload()
      // The ledger too — the action that just succeeded APPENDED to it, and a
      // history card still showing "no decision recorded yet" directly under
      // the approval the officer has just made is the screen contradicting
      // itself about the one record that is meant to be authoritative.
      history.reload()
    } catch (error) {
      setLifecycleAttempt({
        kind: 'failed',
        // The server's own refusal text, verbatim — a 403's decision reason
        // or a 409's named status — never a generic "something went wrong".
        message: error instanceof ApiError ? error.message : fallbackMessage,
      })
    }
  }

  const handleApproveL1 = () =>
    runLifecycleAction(() => api.approveBill(bill.reference, officerComment.trim()), 'Could not approve this bill.')
  const handleApproveL2 = () =>
    runLifecycleAction(() => api.approveLevel2Bill(bill.reference, officerComment.trim()), 'Could not approve this bill.')
  const handleReject = () =>
    runLifecycleAction(
      () => api.rejectBill(bill.reference, rejectReason.trim(), officerComment.trim()),
      'Could not reject this bill.',
    )

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm text-[var(--sgds-color-subtle)] mb-1">
            <Link to="/bills">Bills</Link> / <span className="pt-mono">{bill.reference}</span>
          </p>
          <h1 className="text-3xl pt-mono">{bill.reference}</h1>
        </div>
        <BillStatusBadge status={bill.status} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="pt-card lg:col-span-1">
          <h2 className="text-lg mb-4">Bill</h2>
          <dl className="flex flex-col gap-3 text-sm">
            <Field label="Officer" value={bill.officerId} mono />
            <Field label="Agency" value={bill.agencyCode} />
            <Field label="Bill type" value={billTypeLabel(bill.billType)} />
            <Field label="Amount" value={<Money cents={bill.amount} />} />
            <Field label="Service date" value={formatIsoDate(bill.serviceDate)} strong />
            <Field label="Submission date" value={formatIsoDate(bill.submissionDate)} />
          </dl>
          <p className="text-sm text-[var(--sgds-color-subtle)] mt-4 pt-4 border-t border-[var(--sgds-border-color-muted)]">
            Pro-ration is always defined against the <strong>service date</strong>, never the submission
            date. {serviceSubmissionNote(bill.serviceDate, bill.submissionDate)}
          </p>
          {/*
            ★★★ Rendered ONLY when the server says the two dates resolve to different
            entitlement periods. It is not styled as a warning about the bill — the
            bill is correct — but it is emphasised, because this is the one sentence
            on this screen that a reader could otherwise only take on trust.
          */}
          {entitlementBasisNote(result.data?.entitlementBasis) !== null && (
            <p
              className="text-sm mt-3 p-3 rounded border-l-4 border-l-[var(--sgds-primary-color-default)] bg-[var(--sgds-primary-surface-muted)]"
              data-testid="entitlement-basis-note"
            >
              {entitlementBasisNote(result.data?.entitlementBasis)}
            </p>
          )}
        </div>

        <div className="pt-card lg:col-span-2">
          <h2 className="text-lg mb-4">Pro-ration split and provenance</h2>

          {isComputedBill(bill) ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <div className="pt-well p-4">
                  <p className="text-xs font-semibold uppercase text-[var(--sgds-color-subtle)]">Government subsidy</p>
                  <p className="text-2xl pt-tabular font-semibold mt-1">
                    <Money cents={bill.subsidy} />
                  </p>
                </div>
                <div className="pt-well p-4">
                  <p className="text-xs font-semibold uppercase text-[var(--sgds-color-subtle)]">Officer co-payment</p>
                  <p className="text-2xl pt-tabular font-semibold mt-1">
                    <Money cents={bill.coPayment} />
                  </p>
                </div>
              </div>

              {/*
                WHERE THE RATE CAME FROM, between the money it produced and the
                rule that produced it — the two things it qualifies. Never a page
                banner: on the demonstration estate 2,838 of 5,117 bills are in
                this state, and a notice on 55% of screens is one a reader trains
                themselves to skip.

                ⚠ Every word is the SERVER'S (`rateProvenance.note`, from
                `app.ward_rule_version.note` via
                `services/gateway/src/rate-provenance.ts`). `null` for every MSO
                and CCS bill, and for any CPW/FAW/CWM rate PSD authors in answer
                to Q-34 — so nothing renders on those and no real rate ever
                acquires a synthetic marker.
              */}
              <RateProvenanceNote
                provenance={result.data?.rateProvenance ?? null}
                heading="Where this subsidy rate came from"
              />

              <dl className="flex flex-col gap-3 text-sm">
                <Field label="Applied rule" value={<span className="pt-mono">{bill.appliedRule.id}</span>} />
                <Field label="Rule version" value={String(bill.appliedRule.version)} />
                <Field
                  label={
                    <span className="inline-flex items-center gap-2">
                      Applied rate
                      <SgdsTooltip content="Basis points: 1/100 of a percent. 6000 = 60% of the bill subsidised.">
                        <span
                          tabIndex={0}
                          aria-label="What is a basis point?"
                          className="inline-grid place-items-center w-6 h-6 rounded-full border border-[var(--sgds-border-color-default)] cursor-help"
                        >
                          {/* An information glyph, not a "?" character: the label read "Applied rate?" */}
                          <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
                            <circle cx="8" cy="4.2" r="1.2" fill="currentColor" />
                            <path d="M8 7v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                          </svg>
                        </span>
                      </SgdsTooltip>
                    </span>
                  }
                  value={`${formatBasisPoints(bill.appliedRule.rate)} subsidised`}
                />
                <Field label="Computed at" value={bill.computedAt !== null ? formatInstant(bill.computedAt) : 'Not recorded'} />
                {/* M2: not "Selected on service date" — this row reads the
                    date stored on the bill; it does not claim this system
                    just resolved a rule against it. */}
                <Field label="Service date" value={formatIsoDate(bill.serviceDate)} strong />
              </dl>
            </>
          ) : (
            <ComputePanel
              bill={bill}
              offer={offer}
              computing={computeAttempt.kind === 'computing'}
              failure={computeAttempt.kind === 'failed' ? computeAttempt.message : null}
              confirmingRepair={confirmingRepair}
              onCompute={handleCompute}
              onBeginRepair={() => setConfirmingRepair(true)}
              onCancelRepair={() => setConfirmingRepair(false)}
            />
          )}
        </div>
      </div>

      <BillHistory
        billStatus={bill.status}
        transitions={history.data?.transitions}
        truncated={history.data?.truncated ?? false}
        loading={history.loading}
        error={history.error}
        onRetry={history.reload}
      />

      {/*
        ★ PHASE 10 (Task 36) — Annex A 3/4's own chain, in full, for the first
        time on a screen. `apps/officer-web/src/pages/RecordsPage.tsx` already
        shows ONE delta per delivered record (Task 11); this shows the WHOLE
        running chain for the bill itself.

        ⚠★ **THE "read-only" CLAUSE THIS COMMENT CARRIED IS REPLACED (Wave 1c,
        2026-08-17), and the distinction it drew is still the right one.** It
        read: *"read-only, because an adjustment arrives by file (the HSP intake
        pipeline), never from a keyboard here."* Corrections can now be entered
        here — and they STILL arrive by file. `AdjustmentComposer` below asks
        the SERVER to render a one-record `PRS2-HSP-BILL` delivery, deposit it
        on the counterparty's channel and read it back through the identical
        pipeline: same grammar, same authorisation, same caps, same audit rows.

        **A form that wrote the adjustment effect directly was refused, and
        S-056 is why.** `hsp-adjustment.ts` alone carries the drift comparison,
        the service-date inheritance check, the grace period, the
        `ADJUSTMENT_ORIGINAL_CAPPED` refusal and S-034's cap re-application; a
        second writer beside it would carry whatever its author remembered and
        would be invisible to every test written about the first.
      */}
      {/*
        ★★★ WAVE 13 (P7) - RE-PRORATION, and the sentence that keeps it honest.

        P2C1 §12, Recovery Agency interface row 7: "Alert file on re-prorated HSP
        bills due to changes in officers' entitlement records". This is the
        read-only view of what that produced for THIS bill.

        ⚠ READ-ONLY, and there is no control here on purpose. Re-proration is an
        estate-wide sweep (`POST /api/bills/reprorate-sweep`): re-dividing one
        bill because its officer's entitlement moved, while leaving every other
        bill that officer holds untouched, is not a coherent operation. The same
        argument the adjustments card below makes for itself.
      */}
      <div className="pt-card pt-no-print">
        <h2 className="text-lg mb-4">Re-proration</h2>
        {reprorations.error !== null ? (
          <ErrorCard
            title="Could not load the re-proration history"
            message={reprorations.error}
            onRetry={reprorations.reload}
          />
        ) : reprorations.initialLoading ? (
          <LoadingCard label="Loading re-proration history" rows={2} />
        ) : (reprorations.data?.reprorations.length ?? 0) === 0 ? (
          <EmptyCard
            title="Never re-prorated"
            message="The entitlement this bill was priced under has not changed since it was computed, or the bill predates PRS recording which entitlement produced a split, in which case nothing can be said about it from the record."
            goodNews
          />
        ) : (
          /*
            ⚠ A COMPONENT, not markup inline here. The sentences it renders are
            the deliverable — "REFERRED means PRS changed nothing", the server's
            own refusal verbatim, and "the amount never changed" — and this
            page cannot be mounted in a DOM test (too many live dependencies:
            `BillDetailPage.dom.test.tsx` mounts `ComputePanel` alone). Inline
            markup here would have no coverage at all.
          */
          <ReprorationHistory reprorations={reprorations.data?.reprorations ?? []} />
        )}
      </div>

      {/* Q-54: PSD only, and only for a bill priced under MSO on its service date. Read-only. */}
      {user !== null && mayAdministerReferenceData(user.scope) && result.data?.entitlementBasis?.atService?.scheme === 'MSO' && (
        <MsoBillComparison reference={bill.reference} />
      )}

      <div className="pt-card pt-no-print">
        <h2 className="text-lg mb-4">Adjustments</h2>
        {adjustments.error !== null ? (
          <ErrorCard title="Could not load adjustments" message={adjustments.error} onRetry={adjustments.reload} />
        ) : adjustments.initialLoading ? (
          <LoadingCard label="Loading adjustments" rows={2} />
        ) : (adjustments.data?.adjustments.length ?? 0) === 0 ? (
          <EmptyCard title="No adjustments" message="Nothing has adjusted this bill's original amount." goodNews />
        ) : (
          <div className="pt-table-wrap">
            <table className="pt-table">
              <caption className="sr-only">
                Adjustments applied to this bill, in sequence order: each resulting figure is the amount after the rows above it
              </caption>
              {/*
                ⚠ NOT a sortable table, and this is the exemption the standing rule
                asks to be STATED rather than assumed.

                This is a CORRECTION CHAIN, not a list of adjustments. Every
                "Resulting ..." column is the amount AFTER the rows above it have been
                applied, in `seq` order — adjustment #3's resulting amount is only
                true given #1 and #2 preceding it. Re-ordering by amount, or by type,
                would leave every one of those figures on screen next to the wrong
                predecessor, and the table would say something arithmetically false
                while looking perfectly sorted. `#` is the sequence, and the sequence
                IS the meaning — the same reason `VersionHistory` and the notification
                attempt ledger are exempt.
              */}
              <thead>
                <tr>
                  <th data-unsortable="the correction sequence itself — an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set">#</th>
                  <th data-unsortable="a correction chain: an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set">Type</th>
                  <th data-unsortable="a correction chain: an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set">Delta</th>
                  <th data-unsortable="the amount AFTER the rows above have been applied — re-ordering would print it beside the wrong predecessor">
                    Resulting amount
                  </th>
                  <th data-unsortable="the subsidy AFTER the rows above have been applied — re-ordering would print it beside the wrong predecessor">
                    Resulting subsidy
                  </th>
                  <th data-unsortable="the co-payment AFTER the rows above have been applied — re-ordering would print it beside the wrong predecessor">
                    Resulting co-payment
                  </th>
                  <th data-unsortable="a correction chain: an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set">Rule reproduced</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.data?.adjustments.map((adj: BillAdjustment) => (
                  <tr key={adj.seq}>
                    <td className="pt-tabular">{adj.seq}</td>
                    <td><CategoryBadge kind="billType" value={adj.indicator === 'UA' ? 'UPWARD_ADJUSTMENT' : 'DOWNWARD_ADJUSTMENT'} label={adj.indicator === 'UA' ? 'Upward' : 'Downward'} /></td>
                    <td className="pt-tabular">
                      <Money cents={adj.delta} />
                    </td>
                    <td className="pt-tabular">
                      <Money cents={adj.resulting.amount} />
                    </td>
                    <td className="pt-tabular">
                      <Money cents={adj.resulting.subsidy} />
                    </td>
                    <td className="pt-tabular">
                      <Money cents={adj.resulting.coPayment} />
                    </td>
                    <td className="pt-mono">
                      {adj.appliedRule.id} v{adj.appliedRule.version}
                      {adj.resolutionDrift && (
                        <span className="ml-2 text-xs text-[var(--sgds-danger-color-emphasis)]">
                          (today&rsquo;s rule set now disagrees)
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/*
          ★★ WAVE 1c — Annex A 3/4 from a keyboard. See this card's own header
          for why it composes a DELIVERY rather than writing an adjustment.
          It renders nothing at all when this deployment wires no channel the
          caller may deposit onto.
        */}
        <AdjustmentComposer
          billReference={reference}
          onApplied={() => {
            // Both the bill's own figures and its adjustment chain moved.
            result.reload()
            adjustments.reload()
          }}
        />
      </div>

      {/*
        ★ PHASE 10 (Task 38) — Annex A 7's payment status, in full. THEIR
        vocabulary (paid/partially paid/pending/rejected) on THEIR axis
        (settlement_state) — never our approval `status`, which sits in the
        badge at the top of this page and answers a completely different
        question. Read-only: a payment event arrives from a Payment Agency's
        file, never from a keyboard here.
      */}
      <div className="pt-card pt-no-print">
        <h2 className="text-lg mb-4">Payment status</h2>
        {payments.error !== null ? (
          <ErrorCard title="Could not load payment status" message={payments.error} onRetry={payments.reload} />
        ) : payments.initialLoading ? (
          <LoadingCard label="Loading payment status" rows={2} />
        ) : (
          <>
            <dl className="flex flex-col gap-3 text-sm mb-4">
              <Field label="Settlement state" value={<StateBadge kind="settlement" value={payments.data?.settlementState ?? 'NOT_SETTLED'} />} strong />
              <Field
                label="Paid so far"
                value={<Money cents={payments.data?.paidAmount ?? 0} />}
              />
              <Field
                label="Currently owed"
                value={<Money cents={payments.data?.currentAmount ?? bill.amount} />}
              />
            </dl>
            {(payments.data?.payments.length ?? 0) === 0 ? (
              <EmptyCard title="No payment events yet" message="No Payment Agency has reported an instalment against this bill." goodNews={false} />
            ) : (
              <div className="pt-table-wrap">
                <table className="pt-table">
                  <caption className="sr-only">
                    Payment events reported against this bill by the Payment Agency, in the order they were reported
                  </caption>
                  {/*
                    ⚠ NOT sortable, for the same reason as the adjustment chain above.
                    This is the instalment ledger a Payment Agency reported, in the
                    order it reported it, and "Paid so far" in the panel above is the
                    RUNNING TOTAL of these rows. A reader who re-ordered them would be
                    looking at a payment history that no longer reconciles to the
                    figure beside it.
                  */}
                  <thead>
                    <tr>
                      <th data-unsortable="an instalment ledger in the order the Payment Agency reported it — an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set">
                        Occurred
                      </th>
                      <th data-unsortable="an instalment ledger in the order the Payment Agency reported it — an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set">
                        Status
                      </th>
                      <th data-unsortable="an instalment in a running total — re-ordering would show a payment history that no longer reconciles to Paid so far">
                        Amount
                      </th>
                      <th data-unsortable="an instalment ledger in the order the Payment Agency reported it — an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set">
                        Their reference
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.data?.payments.map((event: BillPaymentEvent, index: number) => (
                      <tr key={`${event.occurredAt}-${index}`}>
                        <td className="pt-tabular">{formatInstant(event.occurredAt)}</td>
                        <td>
                          <StateBadge value={event.status} />
                        </td>
                        <td className="pt-tabular">
                          <Money cents={event.paidAmount} />
                        </td>
                        <td className="pt-mono">{event.sourceReference ?? 'None'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      <DecisionCard
        bill={bill}
        scope={user?.scope ?? null}
        attempt={lifecycleAttempt}
        showRejectForm={showRejectForm}
        rejectReason={rejectReason}
        reasonMaxLength={publishedMaxLength(result.data?.validation, 'reason')}
        officerComment={officerComment}
        commentMaxLength={publishedMaxLength(result.data?.validation, 'comment')}
        onOfficerCommentChange={setOfficerComment}
        onToggleReject={() => {
          setShowRejectForm((v) => !v)
          setShowApproveConfirm(false)
          setLifecycleAttempt({ kind: 'idle' })
        }}
        showApproveConfirm={showApproveConfirm}
        onToggleApprove={() => {
          setShowApproveConfirm((v) => !v)
          // ⚠ Closing the other decision's form as well. Two open confirmations
          // on one bill is a screen asking two questions and accepting one answer.
          setShowRejectForm(false)
          setLifecycleAttempt({ kind: 'idle' })
        }}
        onRejectReasonChange={setRejectReason}
        onApproveL1={handleApproveL1}
        onApproveL2={handleApproveL2}
        onReject={handleReject}
      />

      {/*
        ★★ WAVE 10 — BOTH reads are refreshed, and that is not belt-and-braces.
        A pause changes `app.bill.status` (this page's own read) AND appends to
        `app.bill_transition` (the history card's read); refreshing only the first
        would leave the timeline missing the entry that explains the new status,
        which is the state a reader would report as a bug.
      */}
      <BillPauseCard
        bill={bill}
        scope={user?.scope ?? null}
        onChanged={() => {
          result.reload()
          history.reload()
        }}
      />

      <div>
        <SgdsButton variant="ghost" type="button" onClick={() => navigate('/bills')}>
          Back to bills
        </SgdsButton>
      </div>
    </div>
  )
}

/**
 * The "no split yet" surface — and the way out of C3's trap, which until now
 * existed only as a curl command.
 *
 * ══ THE DEFECT THIS REPLACES ══
 *
 * This panel gated its button on `RECOMPUTABLE_STATUSES` alone, so it was hidden
 * for exactly the bills the recovery path exists to repair, and it explained
 * itself with a sentence the recovery path had made false: *"a bill decided
 * without ever being computed needs an adjustment, not a first-time compute this
 * late"*. The approval queue said the opposite ("open the bill to compute it"),
 * so an APPROVED-and-uncomputed bill had no UI route at all and two screens that
 * contradicted each other about why. C3 itself invoked the discipline that a
 * refusal ships with its way out; this is that way out, shipped.
 *
 * ══ WHY THE REPAIR LOOKS DIFFERENT, AND TAKES TWO CLICKS ══
 *
 * A repair is not a compute that happens later. The bill has already passed a
 * decision it should not have passed without a split, so the money is being
 * written behind a lifecycle that has already moved on. That is why the gateway
 * requires level 2 approval authority with the monetary ceiling binding
 * (`COMPUTE_RECOVERY`, `packages/auth/src/scope.ts`), why the event is audited
 * separately as `BILL_PRORATION_RECOVERY`, and why the officer is shown a
 * warning and asked to confirm rather than handed the same primary button an
 * ordinary compute uses. Making the two look alike would be the more
 * comfortable design and a worse one.
 *
 * ⚠ HOW FAR the lifecycle has moved on differs between the two recoverable
 * statuses, and the copy must say which. On `APPROVED` nothing follows and the
 * figure reaches settlement unchecked; on `AWAITING_L2` a level 2 approver still
 * decides the bill and sees the split. The AUTHORITY is level 2 either way — set
 * by the worse case — but the SENTENCE is not, and asserting the `APPROVED` one
 * on an `AWAITING_L2` bill is a false statement of fact on a money screen. See
 * `RecoveryStage` in `lib/authority.ts`.
 *
 * ══ WHY A FAILED ATTEMPT RENDERS *HERE* AND NOT INSTEAD OF THIS ══
 *
 * A failed compute used to replace this whole panel with a card carrying the
 * server's reason and a "Try again" button wired straight to the compute. On the
 * repair path that button was a ONE-CLICK commit of money onto an
 * already-approved bill: the warning alert, the repair framing, the amount and
 * the two-step confirm were all gone, and the paragraph above — the one saying
 * the officer "is shown a warning and asked to confirm rather than handed the
 * same primary button an ordinary compute uses" — was simply untrue on the one
 * path where an officer is most likely to be pressing again in a hurry.
 *
 * So the reason is rendered above the surface rather than in place of it. A
 * retry is whatever the first attempt was: one button for an ordinary compute,
 * two steps for a repair. It also keeps the trigger MOUNTED, which is what
 * stopped the failure throwing keyboard focus back to `<body>` — the same defect
 * `RepairPanel`'s focus contract was written for, reached by a different route.
 *
 * ⚠ `confirmingRepair` deliberately survives a failure. It is reset on success
 * and on Cancel, and the officer who confirmed a repair that the server then
 * refused is looking at the confirm box, the reason, and both ways forward —
 * state and screen now agree, where before the state said "confirming" and the
 * screen showed a generic error card.
 *
 * ⚠ EXPORTED for its two tests. `BillDetailPage.test.ts` renders it through
 * `renderToStaticMarkup` and asserts the COPY on this surface, which is where
 * every false-statement finding on this screen has landed.
 * `BillDetailPage.dom.test.tsx` mounts it in jsdom and asserts what the SGDS
 * components RECEIVE — the warning alert's `variant` and its stage-dependent
 * `title`, and the `(variant, tone)` pair that keeps the repair's trigger from
 * looking like the ordinary compute's primary button. Neither is reachable from
 * static markup, which is why that half went unasserted until a DOM existed.
 */
export function ComputePanel({
  bill,
  offer,
  computing,
  failure,
  confirmingRepair,
  onCompute,
  onBeginRepair,
  onCancelRepair,
}: {
  bill: Bill
  offer: ComputeOffer | null
  computing: boolean
  /** The server's own reason for a failed attempt on THIS page, or `null`. */
  failure: string | null
  confirmingRepair: boolean
  onCompute: () => void
  onBeginRepair: () => void
  onCancelRepair: () => void
}) {
  return (
    <div className="flex flex-col gap-4">
      {failure !== null && (
        // `pt-well` and the danger emphasis token — the same pairing
        // `DecisionCard` uses for a refused lifecycle action below, and the same
        // one `BillHistory` uses for a load failure. No new token, and no
        // `SgdsAlert`: a second alert stacked above the repair's warning alert
        // would read as two warnings about the bill rather than one warning and
        // one failed attempt.
        <div role="alert" className="pt-well p-4 pt-focus-clear">
          <p className="font-semibold text-[var(--sgds-danger-color-emphasis)]">This bill could not be computed</p>
          <p className="mt-2 text-[var(--sgds-body-color-subtle)]">{failure}</p>
          {/*
            `--sgds-body-color-subtle` on the third line too, NOT
            `--sgds-color-subtle`, and the difference is measured rather than
            assumed. On `.pt-well` (`--sgds-surface-raised`) the two are the same
            token in light — gray-700 on gray-100, **7.04:1** — and diverge in
            dark, where `--sgds-color-subtle` is gray-400 on gray-800 at
            **4.55:1** and body-subtle is gray-300 at **6.56:1**. 4.55 passes AA
            for 14px text by five hundredths, which `ApprovalQueuePage` already
            recorded as "not a margin" on this same surface. Size carries the
            hierarchy here instead of colour.
          */}
          <p className="mt-2 text-sm text-[var(--sgds-body-color-subtle)]">
            Nothing was written to the bill: a compute that fails never records a partial split. The bill is
            exactly as it was, and the controls below are still the way to try again.
          </p>
        </div>
      )}
      <ComputeSurface
        bill={bill}
        offer={offer}
        computing={computing}
        confirmingRepair={confirmingRepair}
        onCompute={onCompute}
        onBeginRepair={onBeginRepair}
        onCancelRepair={onCancelRepair}
      />
    </div>
  )
}

/** The surface itself: nothing to offer, an ordinary compute, or the repair. */
function ComputeSurface({
  bill,
  offer,
  computing,
  confirmingRepair,
  onCompute,
  onBeginRepair,
  onCancelRepair,
}: {
  bill: Bill
  offer: ComputeOffer | null
  computing: boolean
  confirmingRepair: boolean
  onCompute: () => void
  onBeginRepair: () => void
  onCancelRepair: () => void
}) {
  // No compute is possible in any mode, and WHICH of those cases this is decides
  // what may be said. Stated as a property of the bill, not as a permission the
  // officer lacks.
  //
  // ⚠ The single sentence this replaces — "Nothing is owed on a bill in this
  // state, so there is no subsidy/co-payment division to record" — was written
  // for a REJECTED bill and rendered for an unrecognised status too, where it is
  // a fabricated statement about money on the screen an officer opens to check
  // exactly that. `noComputeGround` separates them; see its contract.
  if (offer === null) {
    const copy = noComputeCopy(noComputeGround(bill), billStatusLabel(bill.status).toLowerCase())
    return (
      <div className="pt-unbuilt">
        <p className="font-semibold">{copy.title}</p>
        <p className="mt-2 text-[var(--sgds-body-color-subtle)]">{copy.body}</p>
      </div>
    )
  }

  if (offer.kind === 'COMPUTE') {
    return (
      <div className="pt-unbuilt">
        <p className="font-semibold">Not yet computed</p>
        <p className="mt-2 text-[var(--sgds-body-color-subtle)]">
          No rule has been applied to this bill yet. There is no subsidy, co-payment or rule provenance to show:
          this is a state of the bill, not a missing feature.
        </p>
        {offer.availability.allowed ? (
          <div className="mt-4">
            <SgdsButton variant="primary" type="button" loading={computing} onClick={onCompute}>
              Compute pro-ration
            </SgdsButton>
          </div>
        ) : (
          <p className="mt-4 text-sm text-[var(--sgds-body-color-subtle)]">
            {computeUnavailabilitySentence(offer.availability.why, offer, formatCents)}
          </p>
        )}
      </div>
    )
  }

  // The repair. `SgdsAlert variant="warning"` rather than a hand-rolled amber
  // panel: those tokens are already measured on this canvas (the same component
  // carries the demo-identity notice in `Shell.tsx`), and
  // `sgds-reconciliation.md` forbids inventing a value for a surface like this.
  //
  // ⚠ EVERY SENTENCE BELOW BRANCHES ON `offer.stage`, and that is not polish.
  // `RECOVERY_COMPUTE_STATUSES` admits `AWAITING_L2` as well as `APPROVED`, and
  // this panel used to describe only the second: on an AWAITING_L2 bill it told
  // the officer "a decision has already been recorded against it", "nobody
  // approves the result afterwards" and "no approval step follows it" — three
  // false statements about a bill whose level 2 decision was still to come, the
  // last of them in the confirm box immediately above the commit button. The
  // ledger card further down the same screen simultaneously read "No decision
  // recorded yet", and the Decision card below that read "This bill has cleared
  // level 1 and is awaiting a level 2 decision". See `RecoveryStage`.
  // ⚠ A SEPARATE COMPONENT, and it has to be. `ComputeSurface` returns early
  // twice above this point, so the focus hooks the repair step needs could not
  // legally live here — a hook after a conditional return is the rules-of-hooks
  // violation that breaks on the first render that takes a different branch.
  return (
    <RepairPanel
      bill={bill}
      offer={offer}
      computing={computing}
      confirmingRepair={confirmingRepair}
      onCompute={onCompute}
      onBeginRepair={onBeginRepair}
      onCancelRepair={onCancelRepair}
    />
  )
}

/**
 * The repair step, and the focus contract the two-click gesture depends on.
 *
 * ⚠ THE DEFECT THIS FIXES, driven from a real keyboard in Chrome. Activating
 * "Record the split" with Enter swapped the button for the confirm box — which
 * unmounts the focused element — so `document.activeElement` fell back to
 * `<body>`. A keyboard user was returned to the top of the document, and a
 * screen-reader user was told nothing at all: the control they had just
 * activated vanished, no status message was emitted, and reaching "Confirm
 * repair" meant tabbing in from the start of the page. WCAG 2.1 SC 2.4.3 and
 * 4.1.3. The gesture "worked" only for a pointer.
 *
 * Focus moves to the confirm PANEL rather than to the "Confirm repair" button:
 * this step exists to interrupt, and landing on the commit control would let a
 * second Enter — the one already travelling towards the first button — commit
 * money on an already-approved bill. Landing on the panel announces the sentence
 * naming the bill and the amount, and Tab from there reaches Confirm, then
 * Cancel.
 *
 * Cancelling returns focus to the trigger, because the same unmount happens in
 * reverse and dumping a user who backed out at the top of the page is the same
 * defect with a friendlier cause.
 */
function RepairPanel({
  bill,
  offer,
  computing,
  confirmingRepair,
  onCompute,
  onBeginRepair,
  onCancelRepair,
}: {
  bill: Bill
  offer: Extract<ComputeOffer, { kind: 'COMPUTE_RECOVERY' }>
  computing: boolean
  confirmingRepair: boolean
  onCompute: () => void
  onBeginRepair: () => void
  onCancelRepair: () => void
}) {
  const confirmRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  // Distinguishes "the confirm just opened" from the first render, so nothing
  // grabs focus on page load — an unrequested focus move on arrival is its own
  // accessibility defect.
  const wasConfirming = useRef(false)

  useEffect(() => {
    if (confirmingRepair) {
      wasConfirming.current = true
      confirmRef.current?.focus()
      return
    }
    if (!wasConfirming.current) return
    wasConfirming.current = false

    // ⚠ `sgds-button.focus()` CANNOT BE CALLED IN THIS TICK, and calling it threw
    // rather than failing quietly — caught in the browser, not in review:
    //
    //   TypeError: Cannot read properties of null (reading 'focus')
    //       at SgdsButton.focus (@govtechsg/sgds-web-component)
    //
    // React has only just re-mounted the element, and `SgdsButton.focus()`
    // delegates to an internal ref that its own Lit render populates on a later
    // microtask. A React effect runs before that render, so the ref is still
    // null. `updateComplete` is Lit's published "I have rendered" promise and is
    // the correct thing to wait on; `requestAnimationFrame` is the fallback for
    // an element that is somehow not yet upgraded. Neither branch is wrapped in
    // a `try`: if this starts failing again it should be visible, not swallowed.
    const trigger = triggerRef.current?.querySelector<HTMLElement & { updateComplete?: Promise<unknown> }>('sgds-button')
    if (trigger == null) return
    if (trigger.updateComplete instanceof Promise) {
      void trigger.updateComplete.then(() => trigger.focus())
    } else {
      requestAnimationFrame(() => trigger.focus())
    }
  }, [confirmingRepair])

  const final = offer.stage === 'FINAL'
  return (
    <div className="flex flex-col gap-4">
      <SgdsAlert
        show
        variant="warning"
        title={final ? 'Approved without a pro-ration split' : 'Cleared level 1 without a pro-ration split'}
      >
        This bill is <strong>{billStatusLabel(bill.status)}</strong> and no subsidy/co-payment division was ever
        determined for it. Recording the split now is a <strong>repair</strong>, not an ordinary compute, and it is
        written to the audit trail as its own event against your identity.{' '}
        {final ? (
          <>
            <strong>No approval follows it</strong>: the figure recorded here is the one that reaches settlement.
          </>
        ) : (
          <>
            <strong>A level 2 approver still has to decide this bill</strong>, and will see the split you record here.
          </>
        )}
      </SgdsAlert>

      <div className="pt-unbuilt">
        <p className="font-semibold">Record the pro-ration split (repair)</p>
        <p className="mt-2 text-[var(--sgds-body-color-subtle)]">
          The split is computed from the rule in force on this bill&rsquo;s <strong>service date</strong> (
          {formatIsoDate(bill.serviceDate)}), exactly as an ordinary compute would.{' '}
          {final
            ? 'What differs is that nobody approves the result afterwards, because the bill is already approved. A correction to the amount or the policy after this point is an adjustment bill, not another repair.'
            : 'What differs is that this bill has already passed a level 1 decision without a split, so recording one is a repair, and a repair needs level 2 authority whichever of the two states the bill is in, because the same act on an already-approved bill would reach settlement unchecked.'}
        </p>

        {!offer.availability.allowed ? (
          <p className="mt-4 text-sm text-[var(--sgds-body-color-subtle)]">
            {computeUnavailabilitySentence(offer.availability.why, offer, formatCents)}
          </p>
        ) : confirmingRepair ? (
          <div
            ref={confirmRef}
            // `tabIndex={-1}` makes the panel programmatically focusable without
            // adding a stop to the natural tab order; `role="group"` + a name
            // means landing on it announces what it is rather than reading as a
            // bare container. NOT `role="alert"`: this is a step the officer
            // asked for, not an error, and an alert would also be announced to
            // people who never moved focus here.
            tabIndex={-1}
            role="group"
            aria-label="Confirm the pro-ration repair"
            className="pt-well p-4 mt-4 flex flex-col gap-3 pt-focus-clear">
            {/* ⚠ "a bill of X", never "for X". The amount named here is the
                BILL's total, not the subsidy — which cannot be known until the
                server computes it — and "record the split on BILL-… for SGD
                4,800.00" reads, to someone moving quickly, as recording a
                payment of that amount. */}
            <p className="text-sm">
              Record the pro-ration split on <span className="pt-mono font-semibold">{bill.reference}</span>, a bill of{' '}
              <span className="font-semibold">
                <Money cents={bill.amount} />
              </span>
              ? This bill is already {billStatusLabel(bill.status).toLowerCase()}.{' '}
              {final
                ? 'The subsidy determined here is what the Government pays, and no approval step follows it.'
                : 'The split determined here is the one the level 2 approver will decide on.'}
            </p>
            <div className="flex flex-wrap gap-2">
              <SgdsButton variant="outline" tone="danger" type="button" loading={computing} onClick={onCompute}>
                Confirm repair
              </SgdsButton>
              <SgdsButton variant="ghost" type="button" onClick={onCancelRepair}>
                Cancel
              </SgdsButton>
            </div>
          </div>
        ) : (
          <div className="mt-4" ref={triggerRef}>
            <SgdsButton variant="outline" tone="danger" type="button" onClick={onBeginRepair}>
              Record the split
            </SgdsButton>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * The lifecycle surface — and the screen the I7 finding was raised against.
 *
 * It used to branch on `bill.status` and nothing else, so an HSP_USER holding
 * approval level 0 was offered "Approve (Level 1)" on every SUBMITTED and
 * AWAITING_L1 bill, "Approve (Level 2)" on every AWAITING_L2 one, and "Reject"
 * on both. The 403 arrived and rendered — but it read `APPROVE_L1 requires
 * approval level 1`: an action token and a level number, saying nothing about
 * what the officer should do instead.
 *
 * ⚠ **The server is still the only authority.** `billAuthority` asks
 * `authorise()` — the gateway's own exported function — so this screen stops
 * offering what the gateway would refuse, but the gateway keeps refusing it
 * independently on every route, and `attempt.kind === 'failed'` below still
 * renders that refusal verbatim. Deleting this gate would cost usability and
 * exactly no security, which is the test of whether a client check has quietly
 * become a control.
 */
/**
 * ⚠ EXPORTED for `BillDetailPage.dom.test.tsx` (Wave 24), the same way
 * `ComputePanel` above is. The page as a whole needs the API, the router and a
 * session to mount; this card is where the two money decisions live, and driving
 * it directly is what makes "approve sends nothing until it is confirmed" a
 * checkable claim rather than a reading of the source.
 */
export function DecisionCard({
  bill,
  scope,
  attempt,
  showRejectForm,
  showApproveConfirm,
  onToggleApprove,
  rejectReason,
  reasonMaxLength,
  officerComment,
  commentMaxLength,
  onOfficerCommentChange,
  onToggleReject,
  onRejectReasonChange,
  onApproveL1,
  onApproveL2,
  onReject,
}: {
  bill: Bill
  scope: Scope | null
  attempt: LifecycleAttempt
  showRejectForm: boolean
  showApproveConfirm: boolean
  onToggleApprove: () => void
  rejectReason: string
  /** ★★ WAVE 9 — Annex A 37/38's approval comment, and the ceiling the server published for it. */
  officerComment: string
  commentMaxLength: number | undefined
  onOfficerCommentChange: (value: string) => void
  /**
   * ★★ S-067 — the ceiling the SERVER published on this bill's own read.
   * `undefined` means it published none, and the field then shows no ceiling rather
   * than a remembered one. Threaded as a prop rather than imported, because the only
   * honest source is the response.
   */
  reasonMaxLength: number | undefined
  onToggleReject: () => void
  onRejectReasonChange: (value: string) => void
  onApproveL1: () => void
  onApproveL2: () => void
  onReject: () => void
}) {
  const submitting = attempt.kind === 'submitting'
  const authority: BillAuthority | null = scope === null ? null : billAuthority(scope, bill)
  const level: 1 | 2 = authority?.pending === 'LEVEL_2' ? 2 : 1
  const reasonProblem = rejectReasonProblem(rejectReason, reasonMaxLength)

  const approveWhy =
    authority?.approve != null && !authority.approve.allowed
      ? unavailabilitySentence(authority.approve.why, level === 2 ? 'APPROVE_L2' : 'APPROVE_L1', formatCents)
      : null
  const rejectWhy =
    authority?.reject != null && !authority.reject.allowed ? unavailabilitySentence(authority.reject.why, 'REJECT', formatCents) : null

  return (
    <div className="pt-card">
      <h2 className="text-lg mb-4">Decision</h2>

      {bill.status === 'APPROVED' || bill.status === 'REJECTED' ? (
        <p className="text-[var(--sgds-body-color-subtle)]">
          This bill is <strong>{billStatusLabel(bill.status)}</strong>. No further lifecycle action is available on
          it: a decision already recorded here is not one this screen can undo.
        </p>
      ) : bill.status === 'INFORMATION_REQUESTED' ? (
        /*
          ★★★ WAVE 10 — THE BRANCH THAT WOULD OTHERWISE HAVE LIED.
          Without it a paused bill fell into the `else` below, whose two sentences
          are "awaiting a level 2 decision" and "awaiting a level 1 decision" — so
          this card would have told an officer a paused bill was waiting for their
          decision while every approval route refused it with a 409. That is the
          screen-says-one-thing-server-says-another shape `DecisionCard` exists to
          prevent, arriving through a status the card predated.

          ⚠ It names WHERE THE WAY OUT IS, rather than only stating the refusal.
          A card that says "no decision can be recorded" and stops is a dead end on
          screen even when the product has a way forward — the "ship the refusal
          with its way out" rule. The Resume control is in the card immediately
          below this one.
        */
        <p className="text-[var(--sgds-body-color-subtle)]">
          This bill is <strong>paused</strong> pending information from the healthcare service provider. No
          approval or rejection can be recorded against it while it is paused: it is waiting on the provider,
          not on this office. <strong>Resume it</strong> in the card below to put it back into the exact queue it
          was paused out of; the bill&rsquo;s own history says which queue that is.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-[var(--sgds-body-color-subtle)]">
            {bill.status === 'AWAITING_L2'
              ? 'This bill has cleared level 1 and is awaiting a level 2 decision.'
              : 'This bill is awaiting a level 1 decision.'}
          </p>

          {attempt.kind === 'failed' && (
            <div role="alert" className="pt-well p-3 text-sm text-[var(--sgds-danger-color-emphasis)] pt-focus-clear">
              {plainProse(attempt.message)}
            </div>
          )}

          {/*
            ★★ WAVE 9 — Annex A 37/38's approval COMMENT, ABOVE the buttons and
            OUTSIDE the reject form, deliberately.

            ⚠ Above, because it applies to whichever decision the officer takes and a
            field below the buttons is a field they fill in after pressing one.
            Outside the reject form, because both stories ask for comments on an
            APPROVAL — the case the reason field does not cover.

            ⚠ Rendered only where a decision is actually available. A comment box on
            a bill this officer cannot decide is an invitation to type something
            nothing will record.
          */}
          {(authority?.approve?.allowed === true || authority?.reject?.allowed === true) && (
            <SgdsTextarea placeholder=""
              label="Comment (optional)"
              hintText={officerCommentHint(commentMaxLength)}
              {...(commentMaxLength === undefined ? {} : { maxlength: commentMaxLength })}
              rows={3}
              value={officerComment}
              onSgdsInput={(e) => onOfficerCommentChange(targetValue(e))}
            />
          )}

          {(authority?.approve?.allowed === true || authority?.reject?.allowed === true) && (
            <div className="flex flex-wrap items-center gap-3 pt-focus-clear">
              {authority.approve?.allowed === true && (
                <SgdsButton variant="primary" type="button" onClick={onToggleApprove}>
                  {showApproveConfirm ? 'Cancel approval' : `Approve (Level ${level})`}
                </SgdsButton>
              )}
              {authority.reject?.allowed === true && (
                <SgdsButton variant="outline" tone="danger" type="button" onClick={onToggleReject}>
                  {showRejectForm ? 'Cancel reject' : 'Reject'}
                </SgdsButton>
              )}
            </div>
          )}

          {/*
            ★★★ WAVE 24 — the approval's second step. `role="group"` and a live
            region, so a screen-reader user is told a confirmation appeared
            rather than finding it by exploring.
          */}
          {showApproveConfirm && authority?.approve?.allowed === true && (
            <div className="pt-well p-4 mt-3 flex flex-col gap-3" role="group" aria-label="Confirm approval">
              <p className="max-w-[70ch]" role="status">
                Approve <span className="pt-mono font-semibold">{bill.reference}</span> at level {level}?{' '}
                {level === 2
                  ? 'This completes the lifecycle: the bill becomes approved and an audit record is written against your identity.'
                  : 'This moves the bill to level 2 and writes an audit record against your identity.'}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <SgdsButton
                  variant="primary"
                  type="button"
                  loading={submitting}
                  onClick={level === 2 ? onApproveL2 : onApproveL1}
                >
                  Confirm approval (Level {level})
                </SgdsButton>
                <SgdsButton variant="ghost" type="button" onClick={onToggleApprove}>
                  Cancel
                </SgdsButton>
              </div>
            </div>
          )}

          {/*
            Every unavailable action gets a sentence, INCLUDING on a bill where
            the other one is offered — a missing "Approve" next to a working
            "Reject" reads as a bug unless the screen says why. `formatCents` is
            passed in so the money in "above your approval limit" is rendered by
            the same formatter as every other amount on this page.
          */}
          {(approveWhy !== null || rejectWhy !== null) && (
            <div className="pt-well p-4 flex flex-col gap-3 pt-focus-clear">
              {approveWhy !== null && (
                <p className="text-sm">
                  <strong className="font-semibold">Approve (Level {level}) is not available to you. </strong>
                  <span className="text-[var(--sgds-body-color-subtle)]">{approveWhy}</span>
                </p>
              )}
              {rejectWhy !== null && rejectWhy !== approveWhy && (
                <p className="text-sm">
                  <strong className="font-semibold">Reject is not available to you. </strong>
                  <span className="text-[var(--sgds-body-color-subtle)]">{rejectWhy}</span>
                </p>
              )}
            </div>
          )}

          {showRejectForm && authority?.reject?.allowed === true && (
            <div className="pt-well p-4 flex flex-col gap-3 pt-focus-clear">
              {/*
                I10 — the reason is MANDATORY at the API (`POST .../reject`
                answers 400 for a blank one and 400 again past its bound), so it
                is collected and validated here rather than discovered. The
                rules live in `lib/reject-reason.ts`, read from the gateway's own
                `MAX_REJECT_REASON_LENGTH` and the `CHECK` in
                `010_reject_reason_length.sql`.

                A textarea, not an input: the field is a paragraph explaining a
                refusal to a healthcare provider, and `maxlength` additionally
                gives SGDS's own live character count.
              */}
              <SgdsTextarea placeholder=""
                label="Reason for rejection"
                hintText={rejectReasonHint(reasonMaxLength)}
                {...(reasonMaxLength === undefined ? {} : { maxlength: reasonMaxLength })}
                rows={4}
                required
                invalid={rejectReason.length > 0 && reasonProblem !== null}
                value={rejectReason}
                onSgdsInput={(e) => onRejectReasonChange(targetValue(e))}
              />
              {rejectReason.length > 0 && rejectReasonMessage(reasonProblem) !== null && (
                <p role="alert" className="text-sm text-[var(--sgds-danger-color-emphasis)]">
                  {rejectReasonMessage(reasonProblem)}
                </p>
              )}
              <div>
                <SgdsButton
                  variant="outline"
                  tone="danger"
                  type="button"
                  disabled={!isSendableRejectReason(rejectReason, reasonMaxLength)}
                  loading={submitting}
                  onClick={onReject}
                >
                  Confirm reject
                </SgdsButton>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Field({
  label,
  value,
  mono = false,
  strong = false,
}: {
  label: ReactNode
  value: ReactNode
  mono?: boolean
  strong?: boolean
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-[var(--sgds-color-subtle)]">{label}</dt>
      <dd className={[mono ? 'pt-mono' : '', strong ? 'font-semibold' : ''].filter(Boolean).join(' ')}>{value}</dd>
    </div>
  )
}
